import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BiLaptop, BiMobile } from "react-icons/bi";
import { LiaCommentDots } from "react-icons/lia";
import EmailPreviewFrame from "../../pages/previewEmail/EmailPreviewFrame";
import CommentBox from "./CommentBox";
import { commentService } from "../../services/commentService";
import { toast } from "react-toastify";

const ContentPreview = ({ 
  contentData, 
  htmlContent, 
  cssContent,
  previewMode: initialMode = "desktop",
  onBack,
  contentType = "product",
  documentId: propDocumentId
}) => {
  const navigate = useNavigate();
  const { documentId: paramDocumentId } = useParams();
  const location = useLocation();
  // Use prop documentId first, then param, then location state
  const documentId = propDocumentId || paramDocumentId;
  const [previewMode, setPreviewMode] = useState(initialMode);

  // Comment system state (for product previews)
  const previewRef = useRef(null);
  const [mouseY, setMouseY] = useState(typeof window !== 'undefined' ? window.innerHeight / 2 : 0);
  const [isHovered, setIsHovered] = useState(false);
  const [comments, setComments] = useState([]);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [commentPosition, setCommentPosition] = useState(null);

  // Enable comments only for product content type
  const isProductPreview = contentType === 'product';
  // Determine effective ID for comment API (supports param route and state-based previews)
  const effectiveDocumentId = documentId || (isProductPreview ? contentData?.id : undefined) || location.state?.documentId;

  // Load comments from backend (matching blog preview pattern)
  const loadComments = async () => {
    if (!effectiveDocumentId || !isProductPreview) return;
    try {
      const response = await commentService.getComments('Product', effectiveDocumentId);
      const formattedComments = response.data.map(comment => ({
        id: comment.id,
        text: comment.content,
        position: comment.position_y,
        isSaved: true,
        user: comment.user,
        createdAt: comment.formatted_created_at
      }));
      setComments(formattedComments);
    } catch (error) {
      console.error('Failed to load comments:', error);
      // Don't show error toast for comments as it's not critical
    }
  };

  useEffect(() => {
    if (effectiveDocumentId && isProductPreview) {
      loadComments();
    }
  }, [effectiveDocumentId, isProductPreview]);

  // Track mouse position within preview area for comment placement
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!previewRef.current) return;
      const rect = previewRef.current.getBoundingClientRect();
      if (e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
        setMouseY(e.clientY - rect.top);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleBack = () => {
    if (onBack && typeof onBack === 'function') {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const handleCommentClick = () => {
    setCommentPosition(mouseY);
    setShowCommentBox(true);
  };

  const handleSaveComment = async (text) => {
    if (!text.trim() || !effectiveDocumentId) return;
    try {
      const commentData = {
        content: text.trim(),
        position_x: 0,
        position_y: commentPosition,
      };
      const response = await commentService.createComment('Product', effectiveDocumentId, commentData);
      const created = response.data;
      setComments((prev) => [
        ...prev,
        {
          id: created.id,
          text: created.content,
          position: created.position_y,
          isSaved: true,
          user: created.user, // may be null
          createdAt: created.formatted_created_at
        },
      ]);
      setShowCommentBox(false);
    } catch (err) {
      console.error('Failed to save comment', err);
      toast.error('Failed to save comment. Please try again.');
    }
  };

  const handleEditComment = async (index, newText) => {
    const comment = comments[index];
    if (!comment.id) {
      // Handle local comments that haven't been saved yet
      const updatedComments = [...comments];
      updatedComments[index].text = newText;
      setComments(updatedComments);
      return;
    }
    
    try {
      const commentData = {
        content: newText.trim()
      };
      
      const response = await commentService.updateComment('Product', effectiveDocumentId, comment.id, commentData);
      const updatedComments = [...comments];
      updatedComments[index] = {
        ...updatedComments[index],
        text: response.data.content,
        createdAt: response.data.formatted_created_at
      };
      setComments(updatedComments);
      toast.success('Comment updated successfully!');
    } catch (error) {
      console.error('Failed to update comment:', error);
      toast.error('Failed to update comment. Please try again.');
    }
  };

  const handleDeleteComment = async (index) => {
    const comment = comments[index];
    if (!comment.id) {
      // Handle local comments that haven't been saved yet
      setComments(comments.filter((_, i) => i !== index));
      return;
    }
    
    try {
      await commentService.deleteComment('Product', effectiveDocumentId, comment.id);
      setComments(comments.filter((_, i) => i !== index));
      toast.success('Comment deleted successfully!');
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment. Please try again.');
    }
  };

  // Handle share functionality for local platform products
  const handleShare = () => {
    if (!documentId && contentType === 'product') {
      toast.warning('Cannot share: Product ID not available');
      return;
    }
    
    // Create share URL based on content type
    let shareUrl;
    if (contentType === 'product' && documentId) {
      // Get user ID from localStorage and encode it (base64 encoding) - only for products
      const userId = localStorage.getItem('user_id');
      let encodedUserId = '';
      if (userId) {
        // Encode user ID using base64
        encodedUserId = btoa(userId.toString());
      }
      
      shareUrl = `${window.location.origin}/preview-product-description/${documentId}`;
      if (encodedUserId) {
        shareUrl += `?user_id=${encodedUserId}`;
      }
    } else if (contentType === 'blog' && documentId) {
      // For blogs, don't add user_id
      shareUrl = `${window.location.origin}/preview-blog/${documentId}`;
    } else {
      shareUrl = window.location.href;
    }
    
    // Copy to clipboard with fallback support
    const copyToClipboard = async (text) => {
      // Check if modern Clipboard API is available
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          return;
        } catch (err) {
          // If Clipboard API fails, fall back to execCommand
          console.warn('Clipboard API failed, trying fallback method:', err);
        }
      }

      // Fallback method using execCommand (works in more environments)
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (!successful) {
          throw new Error('execCommand copy failed');
        }
      } catch (err) {
        console.error('Failed to copy text to clipboard:', err);
        throw err;
      }
    };

    copyToClipboard(shareUrl)
      .then(() => {
        toast.success("Link copied to clipboard!");
      })
      .catch(() => {
        toast.error('Failed to copy link. Please copy manually: ' + shareUrl);
      });
  };

  // Check if this is a local platform product (not Shopify)
  const isLocalPlatform = contentType === 'product' && contentData?.source !== 'Shopify' && !contentData?.is_shopify_page;

  return (
    <div className="preview-container">
      <div className="preview-header">
        <button className="back-btn" onClick={handleBack}>
          <ArrowLeft size={16} style={{marginRight: '4px'}} />
          Back to edit
        </button>
        <span className="preview-text">Preview</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Share button for local platform products */}
          {isLocalPlatform && documentId && (
            <button
              className="share-btn"
              onClick={handleShare}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0056b3'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#007bff'}
            >
              Share
            </button>
          )}
          <div className="view-toggle">
            <button
              className={`toggle-btn ${previewMode === "desktop" ? "active" : ""}`}
              onClick={() => setPreviewMode("desktop")}
              aria-pressed={previewMode === 'desktop'}
              aria-label="Desktop view"
            >
              <BiLaptop size={18} />
            </button>
            <button
              className={`toggle-btn ${previewMode === "mobile" ? "active" : ""}`}
              onClick={() => setPreviewMode("mobile")}
              aria-pressed={previewMode === 'mobile'}
              aria-label="Mobile view"
            >
              <BiMobile size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className={`preview-content ${previewMode === "mobile" ? "mobile-view" : ""}`} ref={previewRef} style={{ position: 'relative' }}>
        {/* Email/Product/Blog preview via iframe */}
        <EmailPreviewFrame
          htmlContent={htmlContent}
          cssContent={cssContent}
          previewMode={previewMode}
        />

        {/* Comment overlay only for product previews */}
        {isProductPreview && (
          <>
            {!showCommentBox && (
              <div
                style={{
                  position: "absolute",
                  right: previewMode === 'mobile' ? "-52px" : "-60px",
                  top: `${mouseY}px`,
                  zIndex: 1000,
                  cursor: "pointer",
                  fontSize: "24px",
                  color: isHovered ? "#D04734" : "#666",
                  backgroundColor: isHovered ? "rgba(208, 71, 52, 0.1)" : "rgba(0, 0, 0, 0.1)",
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isHovered ? "0 4px 12px rgba(208, 71, 52, 0.3)" : "0 2px 8px rgba(0,0,0,0.15)",
                  transition: "all 0.2s ease",
                  border: isHovered ? "2px solid #D04734" : "1px solid rgba(0,0,0,0.1)",
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={(e) => { e.stopPropagation(); setCommentPosition(mouseY); setShowCommentBox(true); }}
                aria-label="Add comment"
                title="Add comment"
              >
                <LiaCommentDots />
              </div>
            )}

            {/* Existing Comments */}
            {comments.map((comment, index) => (
              <CommentBox
                key={index}
                position={comment.position}
                commentText={comment.text}
                isSaved={comment.isSaved}
                onSave={(text) => handleEditComment(index, text)}
                onEdit={(newText) => handleEditComment(index, newText)}
                onDelete={() => handleDeleteComment(index)}
                user={comment.user}
                canModify={true}
                rightOffset={previewMode === 'mobile' ? '-12px' : '-206px'}
              />
            ))}

            {/* New Comment Box */}
            {showCommentBox && (
              <CommentBox
                position={commentPosition}
                onSave={handleSaveComment}
                onCancel={() => setShowCommentBox(false)}
                user={null}
                rightOffset={previewMode === 'mobile' ? '-12px' : '-206px'}
              />
            )}
          </>
        )}
      </div>

      <style jsx>{`
        .preview-container {
          display: flex;
          flex-direction: column;
          height: 100vh;
          overflow: hidden;
          background-color: #f5f5f5;
        }
        .preview-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          background-color: #ffffff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          z-index: 10;
        }
        .back-btn {
          display: flex;
          align-items: center;
          background-color: transparent;
          border: none;
          cursor: pointer;
          color: #333;
          font-size: 14px;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background-color 0.2s;
        }
        .preview-text {
          font-weight: bold;
          font-size: 16px;
          color: #333;
        }
        .view-toggle {
          display: flex;
          gap: 8px;
        }
        .toggle-btn {
          background-color: transparent;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 6px 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justifyContent: center;
          transition: all 0.2s;
        }
        .toggle-btn.active {
          background-color: #f0f0f0;
          border-color: #aaa;
        }
        .preview-content {
          flex: 1;
          overflow: auto;
          transition: all 0.3s ease;
        }
        .mobile-view {
          max-width: 375px;
          margin: 0 auto;
          border: 1px solid #ddd;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          height: calc(100vh - 80px);
          margin-top: 10px;
        }
      `}</style>
    </div>
  );
};

export default ContentPreview;